import * as React from "react";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import StyledGrid, { FilterChip, StyledTextSearch } from "../../../shared/components/StyledGrid";
import ExportToExcelButton from "../../../shared/components/ExportToExcelButton";
import { useNavigate } from "react-router-dom";
import { useGetInvociesMutation, useGetInvoicesStatusesQuery, usePrintInvoiceMutation, useSendInvoiceEmailMutation, useUpdateInvoiceStatusMutation } from "../../../services/InsuranceService";
import useFetchWithMsal from "../../../utils/useFetchWithMsal";
import { LoadingPanel } from "../../../shared/components/LoadingPanel";
import { Button, Typography, styled, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Select, Menu, MenuItem, IconButton, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { TableFilter, assessmentClasses, emailStatuses } from "./tableFilter/TableFilter";
import { naicMemberRoleCheck } from "../../../shared/utils/permissions";
import moment from "moment";
import { MultiFieldContainer } from "./InvoicesTab.styled";
import dayjs from "dayjs";
import { getCurrence } from "../../../constants/TablesColumns";
import { AlternateEmail, LocalPrintshop, Refresh } from "@mui/icons-material";
import { buildInvoicesBaseColumns } from "./invoicesColumns";
import { setNaicCode, setMemberName, setStatus, setAssessmentDate, setAssessmentClass, setEmailStatus, setAssessmentId, setInvoiceNumber, setGroupName } from "../../../store/slices/FilterSlice";

const StyledButton = styled(Button)(({ theme, isfull }) => ({
  backgroundColor: isfull ? theme.palette.primary.main : "",
  color: isfull ? theme.palette.info.contrastText : theme.palette.primary.main,
  svg: {
    color: isfull ? theme.palette.info.contrastText : theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    color: '#fff',
    opacity: 0.5,
  }
}));

export const findIdByName = (arr, name) => {
  const found = arr?.find(item => item.name === name);
  return found ? found.id : null;
};

export const InvoicesTab = React.memo((props) => {
  const { isMemberTab, memberNaicCode, onRedirect, disableFilterReset } = props;
  const roleId = useSelector((state) => state?.user?.roleId);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedInvoiceNaicCode, setSelectedInvoiceNaicCode] = useState(null);
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [rowData, setRowData] = useState({});
  const [sortingModel, setSortingModel] = useState();
  const [isAddItemDropdownVisible, setIsAddItemDropdownVisible] = useState(false);
  // When embedded in a Member form we want invoices to load immediately for that member
  const [isAssessmentLoaded, setIsisAssessmentLoaded] = useState(isMemberTab);

  const [isShowClassAPopup, setIsShowClassAPopup] = useState(false);
  const [isShowClassBPopup, setIsShowClassBPopup] = useState(false);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRepeatDialogOpen, setEmailRepeatDialogOpen] = useState(false);

  const { skip } = useFetchWithMsal();
  const [getInovices, { data, isLoading }] = useGetInvociesMutation("", { skip });
  const [sendInvoiceEmail, { isLoading: isInvoiceEmailSending }] = useSendInvoiceEmailMutation("");

  const [printInvoice, { data: invoiceFile, isLoading: isInvoiceLoading }] = usePrintInvoiceMutation("")

  const [updateInvoice, { }] = useUpdateInvoiceStatusMutation("", { skip });

  const navigate = useNavigate();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [printMenuAnchorEl, setPrintMenuAnchorEl] = useState(null);
  const [printMenuInvoice, setPrintMenuInvoice] = useState(null);

  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [actionMenuContext, setActionMenuContext] = useState(null); // { mode: "print"|"email", invoice: row|null }

  const [bulkEmailType, setBulkEmailType] = useState(null);

  useEffect(() => {
    setIsAddItemDropdownVisible(false);
  }, [isShowClassAPopup, isShowClassBPopup]);

  const handleCloseStatus = (id, status) => {
    setSelectedInvoiceId(id);
    setSelectedInvoiceStatus(status)
    setConfirmDialogOpen(true);
  };

  const handleConfirmClose = () => {
    updateInvoice({ id: selectedInvoiceId, statusId: selectedInvoiceStatus })
    setSelectedInvoiceId(null);
    setSelectedInvoiceNaicCode(null)
    setSelectedInvoiceStatus(null);
    setConfirmDialogOpen(false);
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
  };

  const onPopupClose = () => {
    setIsShowClassAPopup(false);
    setIsShowClassBPopup(false)
  };

  // columns defined later once statuses are loaded

  const { naicCode, memberName, status, statusIds, assessmentDate, assessmentId, invoiceNumber, assessmentClass, emailStatus, groupName, paymentOpenStatus } = useSelector(
    (state) => state.filter
  );
  // Unified search state (NAIC Code or Member Name). Reuse existing filter values if present.
  const [searchTerm, setSearchTerm] = useState(naicCode || memberName || "");
  const [debounceTimer, setDebounceTimer] = useState(null);
  const dispatch = useDispatch();
  // NOTE: actual dispatch not used in this tab yet; search triggers fetch directly

  const handleSendSingleEmail = async (id, naicCode, type = "original") => {
    setEmailRepeatDialogOpen(false)
    try {
      const response = await sendInvoiceEmail({
        invoiceFilter: { lateNotice: type },
        invoices: [Number(id)],
      }).unwrap();

      setSnackbarMessage(`Invoice for member with NAIC Code ${naicCode} sent successfully.`);
      setSnackbarSeverity("success");
    } catch (error) {
      setSnackbarMessage(error?.data?.Message);
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const handlePrintSingleInvoice = async (id, naicCode, type = "original") => {
    try {
      const response = await printInvoice({
        invoiceFilter: { lateNotice: type },
        invoices: [Number(id)],
      }).unwrap();

      setSnackbarMessage(`Print process for ${type.replace(/([A-Z])/g, ' $1').trim()} for member with NAIC Code ${naicCode} started successfully.`);
      setSnackbarSeverity("success");
    } catch (error) {
      setSnackbarMessage(error?.message);
      setSnackbarSeverity("error");
    }
    // setSnackbarOpen(true);
  };

  const sentEmail = async () => {
    try {
      const response = await sendInvoiceEmail({
        "invoiceFilter": selectedRows.length === rows.length || selectedRows.length === 0 ? {
          naicCode: naicCode,
          ...buildStatusFilter(),
          assessmentDate: assessmentDate,
          invoiceNumber: invoiceNumber,
          assessmentClass: findIdByName(assessmentClasses, assessmentClass),
          emailStatus: findIdByName(emailStatuses, emailStatus),
        } : null,
        "invoices": (selectedRows.length !== rows.length && selectedRows.length !== 0) ? selectedRows : null
      }).unwrap();

      setSnackbarMessage(`Send invoices for ${response} selected members is started.`);
      setSnackbarSeverity("success");
    } catch (error) {
      setSnackbarMessage(error?.message);
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const handleEmailConfirmOpen = () => {
    setEmailDialogOpen(true);
  };

  const handleEmailConfirmClose = () => {
    setEmailDialogOpen(false);
  };

  const handleEmailRepeatConfirmOpen = (id, naicCode) => {
    setSelectedInvoiceId(id);
    setSelectedInvoiceNaicCode(naicCode)
    setEmailRepeatDialogOpen(true);
  };

  const handleEmailRepeatConfirmClose = () => {
    setEmailRepeatDialogOpen(false);
  };

  const handleEmailSend = async (type = "original") => {
    setEmailDialogOpen(false);
    try {
      // Ensure we send numeric IDs for selected invoices (DataGrid may provide strings)
      const selectedIds = (selectedRows || [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n));

      const allSelected = selectedIds.length === (rows?.length || 0) && selectedIds.length > 0;
      const hasSearch = Boolean((searchTerm || "").trim());
      const useInvoicesArrayForAll = allSelected && hasSearch;

      console.debug("[InvoicesTab] Bulk email - selected IDs:", selectedIds, { allSelected, hasSearch, useInvoicesArrayForAll });

      const payload = {
        invoiceFilter: useInvoicesArrayForAll
          ? { lateNotice: type }
          : (allSelected || selectedIds.length === 0)
            ? {
                naicCode: isMemberTab ? memberNaicCode : naicCode,
                ...buildStatusFilter(),
                assessmentDate: assessmentDate,
                invoiceNumber: invoiceNumber,
                groupName: groupName,
                assessmentId: assessmentId?.id,
                assessmentClass: findIdByName(assessmentClasses, assessmentClass),
                emailStatus: findIdByName(emailStatuses, emailStatus),
                lateNotice: type,
              }
            : { lateNotice: type },
        invoices: useInvoicesArrayForAll
          ? selectedIds
          : selectedIds.length !== (rows?.length || 0) && selectedIds.length !== 0
            ? selectedIds
            : null,
      };

      const response = await sendInvoiceEmail(payload).unwrap();

      setSnackbarMessage(`Send invoices for ${response} selected members is started.`);
      setSnackbarSeverity("success");
    } catch (error) {
      setSnackbarMessage(error?.message);
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const { data: invoicesStatuses } = useGetInvoicesStatusesQuery("");
  const filteredStatuses = invoicesStatuses?.filter(status => status.id !== -1);

  // Helper: build the appropriate status filter object for server payloads
  const buildStatusFilter = (value) => {
    // If filter requests All Open, send blank statusId so server returns all; client will filter open rows locally
    if (value?.statusIsOpen || paymentOpenStatus) return { statusId: "" };
    // If multi-select ids provided, send them as statusIds array
    const ids = value?.statusIds || statusIds;
    if (ids && Array.isArray(ids) && ids.length) return { statusIds: ids };
    // Fallback to single status name -> id mapping
    const statusName = value ? value.status : status;
    const sid = findIdByName(invoicesStatuses, statusName);
    return sid ? { statusId: sid } : {};
  };

  const invoicesColumns = React.useMemo(() => {
    const baseColumns = buildInvoicesBaseColumns({
      handleCloseStatus,
      filteredStatuses: filteredStatuses || [],
      invoicesStatuses: invoicesStatuses || [],
      onRedirect,
    });
    return [
      ...baseColumns,
      {
        field: "email",
        headerName: "",
        width: 30,
        editable: true,
        renderCell: (params) => (
          <MultiFieldContainer>
            <AlternateEmail
              style={{ cursor: "pointer", color: "#003366" }}
              onClick={(e) => {
                setActionMenuAnchorEl(e.currentTarget);
                setActionMenuContext({ mode: "email", invoice: params.row });
              }}
            />
          </MultiFieldContainer>
        ),
      },
      {
        field: "print",
        headerName: "",
        width: 30,
        editable: true,
        renderCell: (params) => (
          <MultiFieldContainer>
            <LocalPrintshop
              style={{ cursor: "pointer", color: "#003366" }}
              onClick={(e) =>
                setActionMenuAnchorEl(e.currentTarget) ||
                setActionMenuContext({ mode: "print", invoice: params.row })
              }
            />
          </MultiFieldContainer>
        ),
      },
    ];
  }, [invoicesStatuses, filteredStatuses, handleCloseStatus, onRedirect]);


  const [selectedRows, setSelectedRows] = useState([]);

  const [selectAll, setSelectAll] = useState(false);

  const [rows, setRows] = useState([]);
  // Track the last applied filters from the TableFilter so free-text search can merge with them
  const [activeFilters, setActiveFilters] = useState(null);

  useEffect(() => {
    // In member context ignore the isAssessmentLoaded gate so the member's invoices load right away
    if (!isAssessmentLoaded && !isMemberTab) return;
    const delay = 0; // fetch immediately for all contexts to avoid perceived double loading
    const timer = setTimeout(() => {
      if (!confirmDialogOpen) {
        // Reuse fetchData to ensure currently active filters (including from TableFilter) are respected
        fetchData();
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    sortingModel?.field,
    sortingModel?.sort,
    confirmDialogOpen,
    memberNaicCode,
    isMemberTab,
    isAssessmentLoaded
  ]);

  useEffect(() => {
    if (selectedRows?.length === rows?.length && rows?.length) {
      setSelectAll(true);
    }
  }, [selectedRows]);

  // useEffect(() => {
  //   setSelectedRows([])
  //   console.log('test');
  // }, [naicCode, status, assessmentDate, invoiceNumber, assessmentClass]);

  const extractIds = (data) => {
    return data?.map(item => item.id);
  }

  useEffect(() => {
    setRows(data?.invoices);
    if (selectAll) {
      setSelectedRows([]);
      setSelectAll(false);
    }
  }, [isLoading, isMemberTab]);

  useEffect(() => {
    if (!isLoading) {
      if (data?.totalCount !== totalCount) {
        setTotalCount(data?.totalCount);
      }
    }
  }, [isLoading, isMemberTab]);

  const fetchData = (value) => {
    // Merge the last applied filters (from the TableFilter) with any incoming overrides (e.g., search term)
    const merged = { ...(activeFilters || {}), ...(value || {}) };

    const body = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      orderBy: sortingModel?.field || "",
      orderByDirection: sortingModel?.sort || "",
      naicCode: isMemberTab ? memberNaicCode : (merged.hasOwnProperty("naicCode") ? merged.naicCode : naicCode),
      memberName: merged.hasOwnProperty("memberName") ? merged.memberName : memberName,
      ...buildStatusFilter(merged),
      assessmentDate: merged.hasOwnProperty("assessmentDate") ? merged.assessmentDate : assessmentDate,
      assessmentId: merged.assessmentId?.id ?? assessmentId?.id,
      assessmentClass: merged.hasOwnProperty("assessmentClass")
        ? findIdByName(assessmentClasses, merged.assessmentClass)
        : findIdByName(assessmentClasses, assessmentClass),
      emailStatus: merged.hasOwnProperty("emailStatus")
        ? findIdByName(emailStatuses, merged.emailStatus)
        : findIdByName(emailStatuses, emailStatus),
      invoiceNumber: merged.hasOwnProperty("invoiceNumber") ? merged.invoiceNumber : invoiceNumber,
      groupName: merged.hasOwnProperty("groupName") ? merged.groupName : groupName,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    getInovices(body);
  };

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const onRowClick = (e) => {
    setRowData(e.row);
    setIsPopupOpen(true);
  };

  const onApplyFilters = (value) => {
    setIsisAssessmentLoaded(true)
    setSelectedRows([])
    setActiveFilters(value || null);
    if (paginationModel.page) {
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    } else {
      fetchData(value);
    }
  };

  // Debounced unified search behavior (numeric -> naicCode, else memberName). Mirrors MembersTab.
  useEffect(() => {
    // Skip unified search behavior in member context; invoices already filtered by memberNaicCode
    if (isMemberTab) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      const trimmed = searchTerm.trim();
      if (!trimmed) {
        // Do not trigger an extra fetch; initial load effect already ran
        return;
      }
      const isDigits = /^\d+$/.test(trimmed);
      const naicVal = isDigits ? trimmed : null;
      const memberVal = isDigits ? null : trimmed;
      fetchData({ naicCode: naicVal, memberName: memberVal });
    }, 400);
    setDebounceTimer(timer);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, isMemberTab]);

  const handleSelectionChange = (newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  };

  const sentInvoiceHandler = (type = "original") => {
    const selectedIds = (selectedRows || [])
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n));

    const allSelected = selectedIds.length === (rows?.length || 0) && selectedIds.length > 0;
    const hasSearch = Boolean((searchTerm || "").trim());
    const useInvoicesArrayForAll = allSelected && hasSearch;

    console.debug("[InvoicesTab] Bulk print - selected IDs:", selectedIds, { allSelected, hasSearch, useInvoicesArrayForAll });

    const invoiceFilter = useInvoicesArrayForAll
      ? { lateNotice: type }
      : {
          ...(allSelected
            ? {
                naicCode: naicCode,
                ...buildStatusFilter(),
                assessmentDate: assessmentDate,
                invoiceNumber: invoiceNumber,
                assessmentClass: findIdByName(assessmentClasses, assessmentClass),
                emailStatus: findIdByName(emailStatuses, emailStatus),
              }
            : {}),
          lateNotice: type,
        };

    const invoices = useInvoicesArrayForAll
      ? selectedIds
      : selectedIds.length !== (rows?.length || 0) && selectedIds.length !== 0
        ? selectedIds
        : null;

    printInvoice({ invoiceFilter, invoices });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handlePrintMenuOpen = (event, invoice) => {
    setPrintMenuAnchorEl(event.currentTarget);
    setPrintMenuInvoice(invoice);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchorEl(null);
    setPrintMenuInvoice(null);
  };

  const handleActionMenuSelect = (noticeType) => {
    setActionMenuAnchorEl(null);

    if (!actionMenuContext) return;

    // Individual actions
    if (actionMenuContext.invoice) {
      if (actionMenuContext.mode === "print") {
        handlePrintSingleInvoice(
          actionMenuContext.invoice.id,
          actionMenuContext.invoice.naicCode,
          noticeType
        );
      } else if (actionMenuContext.mode === "email") {
        handleSendSingleEmail(
          actionMenuContext.invoice.id,
          actionMenuContext.invoice.naicCode,
          noticeType
        );
      }
      return;
    }

    // Bulk actions
    if (actionMenuContext.mode === "print") {
      sentInvoiceHandler(noticeType);
    } else if (actionMenuContext.mode === "email") {
      setBulkEmailType(noticeType); // Save the type
      setEmailDialogOpen(true);     // Open the confirmation dialog
    }
  };

  return (
    <Box sx={!isMemberTab && { height: "100%", width: "100%", margin: "50px auto 0 auto" }}>
      {(isLoading || isInvoiceEmailSending || isInvoiceLoading) && <LoadingPanel />}
      <Box display={"flex"} sx={{ justifyContent: "flex-end" }} mb={3}>
        {isMemberTab && <TableFilter onApplyFilters={onApplyFilters} isMemberTab={isMemberTab} disableFilterReset={disableFilterReset} />}
        {!isMemberTab && <> <Typography variant="h1" color="primary.main" sx={{ marginLeft: "16px" }}>
          Invoices
        </Typography>
          <Box display={"flex"} ml={'auto'} alignItems="center" gap={2}>
            <Button
              variant="outlined"
              onClick={() => fetchData()}
              startIcon={<Refresh />}
            >
              Refresh
            </Button>
            {naicMemberRoleCheck(roleId) && (
              <>
                <ExportToExcelButton
                  rows={rows || []}
                  columns={invoicesColumns}
                  filename="invoices.xlsx"
                  label="Export"
                />
                <StyledButton
                  variant="outlined"
                  isfull='true'
                  onClick={(e) => {
                    setActionMenuAnchorEl(e.currentTarget);
                    setActionMenuContext({ mode: "email", invoice: null });
                  }}
                  disabled={selectedRows?.length === 0}
                  startIcon={<AlternateEmail />}
                >
                  Email ({selectedRows?.length === rows?.length ? data?.totalCount : selectedRows?.length})
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  isfull='true'
                  onClick={(e) => {
                    setActionMenuAnchorEl(e.currentTarget);
                    setActionMenuContext({ mode: "print", invoice: null });
                  }}
                  disabled={selectedRows?.length === 0}
                  startIcon={<LocalPrintshop />}
                >
                  Print ({selectedRows?.length === rows?.length ? data?.totalCount : selectedRows?.length})
                </StyledButton>
              </>
            )}
            <TableFilter onApplyFilters={onApplyFilters} isMemberTab={isMemberTab} />

          </Box>
        </>}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          alignItems: "center",
          marginRight: "15px",
          marginBottom: "15px",
        }}
      >
        {!isMemberTab && (
          <Box sx={{ display: 'flex', maxWidth: 360 }}>
            <StyledTextSearch
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type NAIC code or Member name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchTerm(""); fetchData({ naicCode: null, memberName: null }); }}> <CloseIcon fontSize="inherit" /> </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Box>
        )}
{/*         {naicCode && <FilterChip>NAIC Code: {naicCode}</FilterChip>}
        {groupName && <FilterChip>Group Name: {groupName}</FilterChip>}
        {memberName && <FilterChip>Member Name: {memberName}</FilterChip>} */}
       {/*  {naicCode && (
          <FilterChip>
            NAIC Code: {naicCode}
            <IconButton size="small" onClick={() => { dispatch(setNaicCode(null)); dispatch(setMemberName(null)); setSearchTerm(""); fetchData({ naicCode: null, memberName: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )}
        {memberName && (
          <FilterChip>
            Member Name: {memberName}
            <IconButton size="small" onClick={() => { dispatch(setNaicCode(null)); dispatch(setMemberName(null)); setSearchTerm(""); fetchData({ naicCode: null, memberName: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )}
        {groupName && (
          <FilterChip>
            Group Name: {groupName}
            <IconButton size="small" onClick={() => { dispatch(setGroupName(null)); fetchData({ groupName: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )} */}
        {status && (
          <FilterChip>
            Status: {status}
            <IconButton size="small" onClick={() => { dispatch(setStatus(null)); setActiveFilters(prev => ({ ...(prev || {}), status: null, statusIds: undefined, statusIsOpen: undefined })); fetchData({ status: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )}
        {assessmentClass && (
          <FilterChip>
            Assessment Class: {assessmentClass}
            <IconButton size="small" onClick={() => { dispatch(setAssessmentClass(null)); setActiveFilters(prev => ({ ...(prev || {}), assessmentClass: null })); fetchData({ assessmentClass: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )}
        {emailStatus && (
          <FilterChip>
            Email Status: {emailStatus}
            <IconButton size="small" onClick={() => { dispatch(setEmailStatus(null)); setActiveFilters(prev => ({ ...(prev || {}), emailStatus: null })); fetchData({ emailStatus: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )}
        {assessmentDate && (
          <FilterChip>
            Assessment Date: {assessmentDate}
            <IconButton size="small" onClick={() => { dispatch(setAssessmentDate(null)); setActiveFilters(prev => ({ ...(prev || {}), assessmentDate: null })); fetchData({ assessmentDate: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )}
        {assessmentId && (
          <FilterChip>
            Assessment: {assessmentId?.title}
            <IconButton size="small" onClick={() => { dispatch(setAssessmentId(null)); setActiveFilters(prev => ({ ...(prev || {}), assessmentId: null })); fetchData({ assessmentId: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )}
        {invoiceNumber && (
          <FilterChip>
            Invoice Number: {invoiceNumber}
            <IconButton size="small" onClick={() => { dispatch(setInvoiceNumber(null)); setActiveFilters(prev => ({ ...(prev || {}), invoiceNumber: null })); fetchData({ invoiceNumber: null }); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </FilterChip>
        )}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <StyledGrid
        data={rows}
        width="100%"
        columns={invoicesColumns}
        rowCount={totalCount}
        withFilter={!isMemberTab}
        onSortingModelChange={(model) => setSortingModel(model[0])}
        sortModel={sortingModel ? [sortingModel] : []}
        paginationModel={paginationModel}
        setPaginationModel={handlePaginationModelChange}
        onRowClick={onRowClick}
        withCheckboxSelection={(naicMemberRoleCheck(roleId) && !isMemberTab)}
        checkboxSelection
        selectionModel={selectedRows}
        onRowSelectionModelChange={handleSelectionChange}
        selectAll
      />
      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={() => setActionMenuAnchorEl(null)}
      >
        <MenuItem onClick={() => handleActionMenuSelect("FirstNotice")}>Original Invoice</MenuItem>
        <MenuItem onClick={() => handleActionMenuSelect("SecondNotice")}>30-Day Late Notice</MenuItem>
        <MenuItem onClick={() => handleActionMenuSelect("ThirdNotice")}>60-Day Late Notice</MenuItem>
      </Menu>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancel}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        disableEnforceFocus
      >
        <DialogTitle id="confirm-dialog-title">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Do you really want to change the inovice status?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmClose} color="error" autoFocus>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={emailDialogOpen}
        onClose={handleEmailConfirmClose}
        aria-labelledby="email-dialog-title"
        aria-describedby="email-dialog-description"
        disableEnforceFocus
      >
        <DialogContent>
          <DialogContentText id="email-dialog-description">
            You have selected {selectedRows?.length === rows?.length ? data?.totalCount : selectedRows?.length} invoices for send.<br /><br />
            <b>Type:</b> {
              bulkEmailType === "FirstNotice" ? "Original Invoice" :
                bulkEmailType === "SecondNotice" ? "30-Day Late Notice" :
                  bulkEmailType === "ThirdNotice" ? "60-Day Late Notice" : ""
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailConfirmClose} color="primary">
            Cancel
          </Button>
          <Button onClick={() => handleEmailSend(bulkEmailType)} color="success" autoFocus>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={emailRepeatDialogOpen}
        onClose={handleEmailRepeatConfirmClose}
        aria-labelledby="email-dialog-title"
        aria-describedby="email-dialog-description"
        disableEnforceFocus
      >
        <DialogContent>
          <DialogContentText id="email-dialog-description">
            This invoice has been already sent. Do you want to resend it?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailRepeatConfirmClose} color="primary">
            Cancel
          </Button>
          <Button onClick={() => handleSendSingleEmail(selectedInvoiceId, selectedInvoiceNaicCode)} color="success" autoFocus>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
